/**
 * The fields the user visited during this browser session, most recent first.
 *
 * Feeds the "Recent fields" list the global search shows before anything is
 * typed, so getting back to a field you were just looking at costs one
 * keystroke instead of retyping its name.
 *
 * Only **ids** are kept. They are resolved against the live search index when
 * the box is opened (`resolveRecentFieldEntries`), so a renamed field shows its
 * new name and a deleted or archived one drops out rather than offering a row
 * that leads nowhere.
 *
 * Backed by `sessionStorage`, which is exactly "this session": it survives a
 * reload of the tab and dies with it. Storage is read once, to hydrate, and
 * the in-memory list leads from there — so when storage is unavailable or
 * unwritable (Safari private browsing, blocked site data, quota) the feature
 * still works for the life of the page and only loses its trail on reload.
 */

const {
  RECENT_FIELDS_STORAGE_KEY,
  MAX_RECENT_FIELDS,
} = require('./GlobalSearch.types');
const { addRecentFieldId } = require('./globalSearchHelpers');

const EMPTY_RECENT_FIELD_IDS = [];

// The trail this page is working with. Hydrated from sessionStorage on first
// read and authoritative from then on, so a write that fails (quota, blocked
// site data) costs persistence across a reload but never the list itself.
let recentFieldIds = EMPTY_RECENT_FIELD_IDS;
let isHydrated = false;

// Looked up per call rather than captured at module load, so tests can install
// a stub and so a browser that only exposes storage late still works.
const getSessionStorage = () => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch (error) {
    // Accessing sessionStorage itself throws when site data is blocked.
    return null;
  }
};

const readStoredIds = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(RECENT_FIELDS_STORAGE_KEY);
    if (!raw) {
      return EMPTY_RECENT_FIELD_IDS;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed
          .filter((id) => typeof id === 'string' && id.length > 0)
          .slice(0, MAX_RECENT_FIELDS)
      : EMPTY_RECENT_FIELD_IDS;
  } catch (error) {
    // Unreadable or corrupt value: start the session's list over rather than
    // taking the search down with it.
    return EMPTY_RECENT_FIELD_IDS;
  }
};

const writeStoredIds = (ids) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(RECENT_FIELDS_STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    // Quota or a blocked write — the in-memory list still serves this page.
  }
};

/**
 * The ids of the fields visited this session, most recent first.
 * @returns {Array<string>}
 */
const getRecentFieldIds = () => {
  if (!isHydrated) {
    const stored = readStoredIds();
    if (stored !== null) {
      recentFieldIds = stored;
    }
    isHydrated = true;
  }
  return recentFieldIds;
};

/**
 * Records that a field is being shown to the user.
 *
 * Called on every change of the selected field, so it must be cheap and it
 * must not write when nothing moved — `addRecentFieldId` returns the list
 * unchanged when the field is already at the front, which is the common case.
 *
 * @param {string|number} fieldId
 */
const recordFieldVisit = (fieldId) => {
  const current = getRecentFieldIds();
  const next = addRecentFieldId(current, fieldId);
  if (next === current) {
    return;
  }
  recentFieldIds = next;
  writeStoredIds(next);
};

/**
 * Drops the session's list. Called on sign-out (Header.jsx): sessionStorage
 * outlives a sign-out in the same tab, so without this the next user to sign
 * in here would inherit someone else's trail.
 */
const clearRecentFields = () => {
  recentFieldIds = EMPTY_RECENT_FIELD_IDS;
  isHydrated = true;
  const storage = getSessionStorage();
  try {
    storage?.removeItem(RECENT_FIELDS_STORAGE_KEY);
  } catch (error) {
    // Nothing to do — the in-memory list is already cleared.
  }
};

module.exports = {
  getRecentFieldIds,
  recordFieldVisit,
  clearRecentFields,
};
